import { state } from '../state.js';
import { showToast } from '../utils/toast.js';

export interface BackupSummary {
  connections_count: number;
  mappings_count: number;
  job_presets_count: number;
}

export type BackupScope = 'setup' | 'catalog' | 'users' | 'full';

export type BackupFormat = 'archive' | 'directory';

export interface BackupImportResult {
  success: boolean;
  connections_imported: number;
  mappings_imported: number;
  job_presets_imported: number;
  errors: string[];
}

function getSelectedScope(): BackupScope {
  const radios = document.querySelectorAll('input[name="backup-export-scope"]:checked') as NodeListOf<HTMLInputElement>;
  const value = radios[0]?.value;
  if (value === 'setup' || value === 'catalog' || value === 'users' || value === 'full') {
    return value;
  }
  return state.backup.scope;
}

function getSelectedFormat(): BackupFormat {
  const radios = document.querySelectorAll('input[name="backup-export-format"]:checked') as NodeListOf<HTMLInputElement>;
  const value = radios[0]?.value;
  return value === 'directory' ? 'directory' : 'archive';
}

function updateRestoreUI(): void {
  const zone = document.getElementById('backup-restore-dropzone');
  const status = document.getElementById('backup-restore-file-status');
  const preflight = document.getElementById('backup-preflight-card');
  const preflightBody = document.getElementById('backup-preflight-body');
  const errorBox = document.getElementById('backup-restore-error');

  if (zone) {
    zone.classList.toggle('dropzone-active', state.backup.restore.loading);
  }

  if (status) {
    status.textContent = state.backup.restore.fileName
      ? `Selected: ${state.backup.restore.fileName}`
      : 'Drag a .akeneopack or .zip package here, or click to browse';
  }

  if (errorBox) {
    errorBox.style.display = state.backup.restore.error ? 'block' : 'none';
    errorBox.textContent = state.backup.restore.error || '';
  }

  if (preflight && preflightBody) {
    const manifest = state.backup.restore.manifest;
    if (manifest && state.backup.restore.packageDir) {
      preflight.style.display = 'block';
      const counts = manifest.entity_counts || {};
      const countText = Object.entries(counts)
        .map(([type, count]) => `<li>${escapeHtml(type)}: <strong>${count}</strong></li>`)
        .join('');
      preflightBody.innerHTML = `
        <div class="modal-info-value">
          <strong>Source:</strong> ${escapeHtml(manifest.source_pim_url || 'unknown')}<br>
          <strong>Scopes:</strong> ${escapeHtml((manifest.scopes || []).join(', '))}<br>
          <strong>Format:</strong> ${escapeHtml(manifest.format || 'archive')}<br>
          <strong>Total files:</strong> ${manifest.total_files ?? 'unknown'}
        </div>
        <ul style="margin: 0.5rem 0 0 1rem;">${countText || '<li>No entity counts</li>'}</ul>
      `;
    } else {
      preflight.style.display = 'none';
      preflightBody.innerHTML = '';
    }
  }
}

function updateScopeFormatUI(): void {
  document.querySelectorAll('input[name="backup-export-scope"]').forEach((radio) => {
    const input = radio as HTMLInputElement;
    input.checked = input.value === state.backup.scope;
  });
  document.querySelectorAll('input[name="backup-export-format"]').forEach((radio) => {
    const input = radio as HTMLInputElement;
    input.checked = input.value === state.backup.format;
  });
}

/**
 * Fetch current backup summary and update the modal counters.
 */
export async function fetchBackupSummary(): Promise<BackupSummary | null> {
  try {
    const res = await fetch('/api/v1/backup/summary');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const summary: BackupSummary = data.summary || {
      connections_count: 0,
      mappings_count: 0,
      job_presets_count: 0,
    };

    const connEl = document.getElementById('backup-summary-connections');
    const mapEl = document.getElementById('backup-summary-mappings');
    const jobEl = document.getElementById('backup-summary-jobs');

    if (connEl) connEl.textContent = String(summary.connections_count);
    if (mapEl) mapEl.textContent = String(summary.mappings_count);
    if (jobEl) jobEl.textContent = String(summary.job_presets_count);

    return summary;
  } catch (err: any) {
    showToast(`Failed to load backup summary: ${err.message}`, 'error');
    return null;
  }
}

/**
 * Open the backup export/import modal.
 */
export function openBackupModal(): void {
  const modal = document.getElementById('backup-modal');
  if (modal) {
    modal.style.display = 'flex';
    void fetchBackupSummary();
    resetRestoreState();
    updateScopeFormatUI();
  }
}

/**
 * Close the backup export/import modal.
 */
export function closeBackupModal(): void {
  const modal = document.getElementById('backup-modal');
  if (modal) modal.style.display = 'none';
}

export function setBackupScope(scope: BackupScope): void {
  state.backup.scope = scope;
  updateScopeFormatUI();
}

export function setBackupFormat(format: BackupFormat): void {
  state.backup.format = format;
  updateScopeFormatUI();
}

export function setBackupDryRun(dryRun: boolean): void {
  state.backup.restore.dryRun = dryRun;
  const checkbox = document.getElementById('backup-restore-dry-run') as HTMLInputElement | null;
  if (checkbox) checkbox.checked = dryRun;
}

/**
 * Export and download the backup bundle in the selected scope and format.
 */
export async function exportBackup(): Promise<void> {
  const scope = getSelectedScope();
  const format = getSelectedFormat();
  state.backup.scope = scope;
  state.backup.format = format;

  const includeMedia = scope === 'catalog' || scope === 'full';

  try {
    if (format === 'directory') {
      const res = await fetch(
        `/api/v1/packages/export?scope=${encodeURIComponent(scope)}&format=directory&include_media=${includeMedia}`
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: `HTTP ${res.status}` }));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      showToast(`Package exported to server directory: ${data.package_dir || 'unknown'}`, 'success');
      return;
    }

    const res = await fetch(
      `/api/v1/packages/export?scope=${encodeURIComponent(scope)}&format=archive&include_media=${includeMedia}`
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: `HTTP ${res.status}` }));
      throw new Error(err.message || `HTTP ${res.status}`);
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const filename = `akeneo-tool-${scope}-package.akeneopack`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    showToast(`Backup downloaded as ${filename}`, 'success');
  } catch (err: any) {
    showToast(`Export failed: ${err.message}`, 'error');
  }
}

export function handleBackupDragOver(e: DragEvent): void {
  e.preventDefault();
  e.stopPropagation();
  const zone = document.getElementById('backup-restore-dropzone');
  zone?.classList.add('dropzone-dragover');
}

export function handleBackupDragLeave(e: DragEvent): void {
  e.preventDefault();
  e.stopPropagation();
  const zone = document.getElementById('backup-restore-dropzone');
  zone?.classList.remove('dropzone-dragover');
}

export function handleBackupDrop(e: DragEvent): void {
  e.preventDefault();
  e.stopPropagation();
  const zone = document.getElementById('backup-restore-dropzone');
  zone?.classList.remove('dropzone-dragover');

  const files = e.dataTransfer?.files;
  if (files && files.length > 0) {
    setRestoreFile(files[0]);
  }
}

export function handleBackupFileSelect(e: Event): void {
  const input = e.target as HTMLInputElement;
  if (input.files && input.files.length > 0) {
    setRestoreFile(input.files[0]);
  }
}

function setRestoreFile(file: File): void {
  state.backup.restore.file = file;
  state.backup.restore.fileName = file.name;
  state.backup.restore.error = null;
  state.backup.restore.manifest = null;
  state.backup.restore.packageDir = null;
  updateRestoreUI();
  showToast(`Package selected: ${file.name}`, 'info', 2000);
}

export function resetRestoreState(): void {
  state.backup.restore = {
    file: null,
    fileName: '',
    dryRun: true,
    packageDir: null,
    manifest: null,
    loading: false,
    error: null,
  };
  state.backup.importResult = null;
  const preflight = document.getElementById('backup-preflight-card');
  const resultBox = document.getElementById('backup-import-result');
  if (preflight) preflight.style.display = 'none';
  if (resultBox) resultBox.style.display = 'none';
  updateRestoreUI();
}

/**
 * Upload the selected package and run a dry-run pre-flight summary.
 */
export async function preflightRestore(): Promise<void> {
  const file = state.backup.restore.file;
  if (!file) {
    showToast('Please select a package file to restore', 'warning');
    return;
  }

  state.backup.restore.loading = true;
  state.backup.restore.error = null;
  updateRestoreUI();

  try {
    const formData = new FormData();
    formData.append('package', file, file.name);

    const uploadRes = await fetch('/api/v1/packages/upload', {
      method: 'POST',
      body: formData,
    });

    if (!uploadRes.ok) {
      const err = await uploadRes.json().catch(() => ({ message: `HTTP ${uploadRes.status}` }));
      throw new Error(err.message || `HTTP ${uploadRes.status}`);
    }

    const uploadData = await uploadRes.json();
    if (!uploadData.success || !uploadData.package_dir) {
      throw new Error(uploadData.message || 'Package upload failed');
    }

    state.backup.restore.packageDir = uploadData.package_dir;

    const summaryRes = await fetch(
      `/api/v1/packages/summary?path=${encodeURIComponent(uploadData.package_dir)}`
    );
    if (!summaryRes.ok) {
      const err = await summaryRes.json().catch(() => ({ message: `HTTP ${summaryRes.status}` }));
      throw new Error(err.message || `HTTP ${summaryRes.status}`);
    }

    const summaryData = await summaryRes.json();
    state.backup.restore.manifest = summaryData.summary || null;
    updateRestoreUI();
    showToast('Pre-flight summary loaded. Review and confirm restore.', 'success');
  } catch (err: any) {
    state.backup.restore.error = err.message;
    updateRestoreUI();
    showToast(`Pre-flight failed: ${err.message}`, 'error');
  } finally {
    state.backup.restore.loading = false;
    updateRestoreUI();
  }
}

/**
 * Confirm restore (or dry-run) for the uploaded package.
 */
export async function confirmRestore(): Promise<void> {
  const packageDir = state.backup.restore.packageDir;
  if (!packageDir) {
    showToast('No package uploaded. Run pre-flight first.', 'warning');
    return;
  }

  state.backup.restore.loading = true;
  updateRestoreUI();

  try {
    const dryRun = state.backup.restore.dryRun;
    const res = await fetch('/api/v1/packages/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        path: packageDir,
        dry_run: dryRun,
      }),
    });

    const data = await res.json();
    state.backup.importResult = data;

    const resultBox = document.getElementById('backup-import-result');
    const resultBadge = document.getElementById('backup-import-result-badge');
    const resultBody = document.getElementById('backup-import-result-body');

    if (resultBox) resultBox.style.display = 'block';
    if (resultBadge) {
      resultBadge.textContent = data.success
        ? dryRun
          ? 'Dry-Run Successful'
          : 'Restore Successful'
        : 'Restore Completed with Errors';
    }

    const metrics = data.metrics || {};
    const errorLines = (data.errors || []).length
      ? `<ul style="margin: 0.5rem 0 0 1rem;">${(data.errors as string[]).map((e) => `<li>${escapeHtml(e)}</li>`).join('')}</ul>`
      : '';

    if (resultBody) {
      resultBody.innerHTML = `
        <div class="modal-info-value">
          Mode: <strong>${dryRun ? 'Dry-Run' : 'Live'}</strong><br>
          Total: <strong>${metrics.total ?? 0}</strong>,
          Processed: <strong>${metrics.processed ?? 0}</strong>,
          Created: <strong>${metrics.created ?? 0}</strong>,
          Updated: <strong>${metrics.updated ?? 0}</strong>,
          Failed: <strong>${metrics.failed ?? 0}</strong>.
        </div>
        ${errorLines}
      `;
    }

    if (data.success) {
      showToast(dryRun ? 'Dry-run completed successfully' : 'Package restored successfully', 'success');
      if (!dryRun) {
        resetRestoreState();
      }
    } else {
      showToast(`Restore completed with ${(data.errors || []).length} error(s)`, 'warning');
    }
  } catch (err: any) {
    state.backup.restore.error = err.message;
    updateRestoreUI();
    showToast(`Restore failed: ${err.message}`, 'error');
  } finally {
    state.backup.restore.loading = false;
    updateRestoreUI();
  }
}

/**
 * Read the selected file and import the backup bundle (legacy JSON/YAML profile bundle).
 */
export async function importBackup(): Promise<void> {
  const fileInput = document.getElementById('backup-import-file') as HTMLInputElement | null;
  const resultBox = document.getElementById('backup-import-result');
  const resultBadge = document.getElementById('backup-import-result-badge');
  const resultBody = document.getElementById('backup-import-result-body');

  if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
    showToast('Please select a backup file to import', 'warning');
    return;
  }

  const file = fileInput.files[0];
  const extension = file.name.split('.').pop()?.toLowerCase();
  const detectedFormat: 'json' | 'yaml' = extension === 'yaml' || extension === 'yml' ? 'yaml' : 'json';

  let content: string;
  try {
    content = await file.text();
  } catch (err: any) {
    showToast(`Failed to read file: ${err.message}`, 'error');
    return;
  }

  try {
    const res = await fetch('/api/v1/backup/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        bundle: content,
        format: detectedFormat,
      }),
    });

    const data = await res.json();
    const result: BackupImportResult = data.result || data;

    if (resultBox) resultBox.style.display = 'block';
    if (resultBadge) {
      resultBadge.textContent = result.success ? 'Import Successful' : 'Import Completed with Errors';
    }

    const errorLines = result.errors?.length
      ? `<ul style="margin: 0.5rem 0 0 1rem;">${result.errors.map((e) => `<li>${escapeHtml(e)}</li>`).join('')}</ul>`
      : '';

    if (resultBody) {
      resultBody.innerHTML = `
        <div class="modal-info-value">
          <strong>${result.connections_imported}</strong> connection profiles imported,
          <strong>${result.mappings_imported}</strong> mappings imported,
          <strong>${result.job_presets_imported}</strong> job presets imported.
        </div>
        ${errorLines}
      `;
    }

    if (result.success) {
      showToast('Backup imported successfully', 'success');
      await fetchBackupSummary();
    } else {
      showToast(`Backup import completed with ${result.errors.length} error(s)`, 'warning');
    }
  } catch (err: any) {
    showToast(`Import failed: ${err.message}`, 'error');
    if (resultBox) resultBox.style.display = 'block';
    if (resultBadge) resultBadge.textContent = 'Import Failed';
    if (resultBody) resultBody.textContent = err.message;
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
